/**
 * Ecran de dessin. Coquille React mince : tout le rendu et toute la capture
 * vivent dans DrawingEngine. Ce composant ne se re-rend que sur des evenements
 * rares (changement d'outil, fin de trait, ajout d'une couleur), jamais pendant
 * un trait.
 *
 * Disposition de la barre d'outils : seuls la taille et le bouton de validation
 * prennent toute la largeur. La palette et les outils se contentent de leur
 * contenu et restent centres : etires, ils formaient une grille de gros pavés
 * de texte ou plus rien ne ressortait.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  BRUSH_SIZES,
  DEFAULT_BRUSH_SIZE_INDEX,
  DEFAULT_PALETTE,
  brushPresetIndex,
  MAX_DRAWING_BYTES,
  encodeDrawing,
  fitToBudget,
  type DrawZone,
} from '@retusch/shared';
import { DrawingEngine, type DrawingPhase, type ToolId } from './DrawingEngine.js';
import type { Stroke } from '@retusch/shared';
import type { DevOptionsStore } from './devOptions.js';
import { TimerRing } from '../ui/TimerRing.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { MAX_BRUSH_SIZE, MIN_BRUSH_SIZE } from './canvas/renderConfig.js';
import { useT } from '../locales/useT.js';
import {
  BrushIcon,
  CheckIcon,
  EyedropperIcon,
  FillIcon,
  HandIcon,
  ToolboxIcon,
  UndoIcon,
} from '../ui/icons.js';
import { ZoomControl } from './ZoomControl.js';
import { useCompact } from '../ui/useCompact.js';
import type { TranslationKey } from '../locales/index.js';
import { observeSize } from './canvas/observeSize.js';

export interface DrawingResult {
  readonly buffer: ArrayBuffer;
  readonly palette: readonly string[];
}

export interface DrawingBoardProps {
  readonly image: HTMLImageElement;
  readonly zone: DrawZone;
  readonly durationMs: number;
  readonly dev: DevOptionsStore;
  readonly onSubmit: (result: DrawingResult) => void;
  /**
   * Horodatage serveur de fin de phase. Quand il est fourni, le compte a
   * rebours suit l'heure du serveur et non une duree locale : c'est ce qui
   * aligne les onglets. La sandbox l'omet et retombe sur un timer local.
   */
  readonly endsAt?: number | null;
  /** Libelle du bouton de soumission. */
  readonly submitLabel?: string | undefined;
  /** Manche en cours, affichee dans le bandeau. Absente en sandbox. */
  readonly roundLabel?: string | undefined;
  /**
   * Panneau d'attente affiche A LA PLACE de la barre d'outils une fois le
   * dessin rendu. Le canvas, lui, reste monte : on veut continuer a voir sa
   * propre retouche pendant qu'on attend les autres, pas un ecran vide.
   */
  readonly waiting?: ReactNode;
  /**
   * Passe a true quand la phase de dessin est finie cote SERVEUR. C'est la
   * seule source d'expiration fiable : rAF et setTimeout sont tous deux brides
   * dans un onglet d'arriere-plan, et un dessin jamais soumis se traduit par
   * une zone blanche chez le devineur.
   */
  readonly deadlinePassed?: boolean;
  /**
   * Quelqu'un regarde ce dessinateur : le plateau emet alors un instantane de
   * son travail toutes les PREVIEW_INTERVAL_MS.
   *
   * C'est une exception assumee a la regle « un seul message par phase de
   * dessin » : elle ne s'active QUE si un spectateur est effectivement la, et
   * s'arrete des qu'il s'en va. Sans spectateur, ce plateau n'emet rien avant
   * la validation, exactement comme avant.
   */
  readonly watched?: boolean;
  readonly onPreview?: ((result: DrawingResult) => void) | undefined;
  /**
   * Mode collaboratif : le travail des joueurs precedents sur ce tableau.
   *
   * Peint en fond FIGE, sous les traits du joueur : on complete, on ne defait
   * pas. Chaque couche porte la palette de son auteur, faute de quoi une teinte
   * prelevee a la pipette se rendrait avec un repli.
   */
  readonly base?: ReadonlyArray<{ strokes: readonly Stroke[]; palette: readonly string[] }>;
}

/**
 * Battement des apercus. Assez lent pour que le cout reste marginal (un dessin
 * fait quelques kilo-octets), assez rapide pour qu'on voie le dessin se faire
 * plutot qu'apparaitre.
 */
const PREVIEW_INTERVAL_MS = 2000;

/**
 * Fond de secours, le temps que le moteur existe.
 *
 * La vraie valeur est celle du moteur (`getBaseColor`) : la couleur la plus
 * presente sous le masque. Elle depend du tableau et de la zone, elle ne peut
 * donc pas etre une constante.
 */
const FALLBACK_BASE = '#ffffff';

/** Panneaux de la barre compacte. Un seul ouvert a la fois. */
type PanelId = 'none' | 'color' | 'tool' | 'size';

const PANEL_LABEL: Record<Exclude<PanelId, 'none'>, TranslationKey> = {
  color: 'tool.colors',
  tool: 'tool.tools',
  size: 'tool.size',
};

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Un champ de SAISIE a-t-il le focus ? Le chat ne doit pas piloter les outils.
 *
 * Un slider n'est pas une saisie : le compter comme tel rendait les raccourcis
 * inertes des qu'on avait touche a la taille du pinceau, jusqu'a ce qu'on
 * reclique ailleurs. Seuls les champs ou l'on tape du texte bloquent.
 */
/**
 * Taille visee par un raccourci chiffre, ou null.
 *
 * On lit `code`, la TOUCHE PHYSIQUE, et non `key`, le caractere produit. Sur un
 * clavier AZERTY la rangee du haut donne `&`, `é`, `"` sans Majuscule : les
 * raccourcis 1/2/3 y etaient donc simplement inertes, alors qu'ils sont
 * annonces dans l'infobulle. `Digit1` designe la meme touche partout, quelle
 * que soit la disposition.
 *
 * `key` reste accepte en repli (QWERTY, AZERTY avec Majuscule, pave numerique
 * exotique), et les raccourcis de LETTRES continuent, eux, de passer par `key` :
 * on veut la lettre que le joueur a tapee, pas la position qu'elle occupe sur
 * un clavier americain.
 */
function sizeShortcut(event: KeyboardEvent): number | null {
  const physical = /^(?:Digit|Numpad)([123])$/.exec(event.code);
  if (physical !== null) return Number(physical[1]) - 1;
  if (event.key === '1' || event.key === '2' || event.key === '3') {
    return Number(event.key) - 1;
  }
  return null;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.tagName === 'TEXTAREA') return true;
  if (target instanceof HTMLInputElement) return target.type !== 'range';
  return false;
}

export function DrawingBoard({
  image,
  zone,
  durationMs,
  dev,
  onSubmit,
  endsAt = null,
  submitLabel,
  roundLabel,
  waiting = null,
  deadlinePassed = false,
  watched = false,
  onPreview,
  base,
}: DrawingBoardProps): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<DrawingEngine | null>(null);
  const submittedRef = useRef(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const [palette, setPalette] = useState<readonly string[]>(DEFAULT_PALETTE);
  const [baseColor, setBaseColor] = useState<string>(FALLBACK_BASE);
  const [outsideZone, setOutsideZone] = useState(false);
  /**
   * Combien de pastilles sont AFFICHEES.
   *
   * La palette declaree grandit avec les couleurs prelevees a la pipette ou
   * choisies librement : les autres clients en ont besoin, un trait ne se rend
   * pas sans sa couleur. Mais elles n'ont rien a faire dans la bande de
   * pastilles, qui finissait longue comme le bras au bout d'une manche. Seules
   * les couleurs du TABLEAU se choisissent d'un clic ; les autres se
   * reprennent a la pipette, la ou on les a vues.
   */
  const swatchCount = useRef<number | null>(null);
  const [tool, setToolState] = useState<ToolId>('brush');
  const [colorIndex, setColorIndexState] = useState(0);
  /**
   * Teinte affichee dans le selecteur libre et l'apercu de taille. Distincte de
   * la palette : une couleur choisie librement n'y entre qu'au premier trait,
   * et n'y apparait donc pas comme pastille.
   */
  const [currentColor, setCurrentColor] = useState<string>(DEFAULT_PALETTE[0] ?? '#ffffff');
  const [canUndo, setCanUndo] = useState(false);
  const [phase, setPhase] = useState<DrawingPhase>('overview');
  const [frozen, setFrozen] = useState(false);
  const [brushSize, setBrushSize] = useState<number>(BRUSH_SIZES[DEFAULT_BRUSH_SIZE_INDEX] ?? 10);
  /** Zoom courant, tenu a jour par le moteur (pincement, molette, curseur). */
  const [zoom, setZoom] = useState(1);

  /**
   * Ecran etroit : la barre d'outils se replie en une rangee, et chaque
   * commande ouvre son panneau. Un seul a la fois.
   */
  const compact = useCompact();
  const [panel, setPanel] = useState<PanelId>('none');

  /**
   * Preset allume : deduit de la taille EXACTE, jamais du dernier bouton
   * clique. Pousser le curseur au-dela du moyen allume donc le moyen, puis le
   * grand, et les deux commandes cessent de se contredire.
   */
  const sizeIndex = brushPresetIndex(brushSize);

  // Le moteur est construit une fois : il lit la valeur par une ref, et la
  // suit ensuite si la fenetre change de format.
  const compactRef = useRef(false);
  compactRef.current = compact;
  useEffect(() => {
    engineRef.current?.setLoupe(compact);
  }, [compact]);



  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const engine = new DrawingEngine(
      {
        canvas,
        image,
        zone,
        dev,
        skipIntro: prefersReducedMotion(),
        ...(base === undefined ? {} : { base }),
      },
      {
        onPaletteChange: (next) => {
          swatchCount.current ??= next.length;
          setPalette([...next]);
        },
        onHistoryChange: (state) => setCanUndo(state.canUndo),
        onPhaseChange: setPhase,
        onColorPicked: (index, color) => {
          setColorIndexState(index);
          setCurrentColor(color);
          setToolState('brush');
        },
        onZoomChange: setZoom,
        onOutsideZoneChange: setOutsideZone,
      },
    );

    engineRef.current = engine;
    // Le fond depend du tableau et de la zone : la pastille d'effacement ne
    // peut le connaitre qu'une fois le moteur construit.
    setBaseColor(engine.getBaseColor());
    engine.setLoupe(compactRef.current);
    engine.start();

    // La taille du canvas change aussi SANS evenement de fenetre (barre
    // d'outils qui grandit, panneau qui s'ouvre) : on observe la boite.
    const stopResize = observeSize(canvas, () => engine.resize());

    return () => {
      stopResize();
      engine.destroy();
      engineRef.current = null;
    };
  }, [image, zone, dev]);

  const submit = useCallback(() => {
    const engine = engineRef.current;
    if (engine === null || submittedRef.current) return;
    submittedRef.current = true;
    setFrozen(true);
    engine.freeze();

    const raw = engine.getDrawing();
    // Garantie de taille avant encodage : au-dela de MAX_DRAWING_BYTES le
    // serveur rejetterait le dessin, et son auteur perdrait la manche.
    const drawing = fitToBudget(raw, MAX_DRAWING_BYTES);
    const buffer = encodeDrawing(drawing);

    const points = drawing.reduce((total, stroke) => total + stroke.points.length, 0);
    console.info(
      `[retusch] dessin soumis : ${drawing.length} trait(s), ${points} point(s), ` +
        `${buffer.byteLength} octets (${(buffer.byteLength / 1024).toFixed(2)} Ko), ` +
        `limite ${MAX_DRAWING_BYTES / 1024} Ko`,
    );
    if (drawing !== raw) {
      console.warn('[retusch] dessin decime pour tenir dans le budget reseau');
    }
    onSubmitRef.current({ buffer, palette: engine.getPalette() });
  }, []);

  /**
   * Instantanes pour les spectateurs.
   *
   * On n'emet que si le dessin a CHANGE depuis le dernier envoi : un joueur qui
   * reflechit, ou qui a fini sans valider, ne doit rien coûter. La comparaison
   * porte sur le nombre de traits et de points, ce qui suffit ici : on ne
   * repasse jamais deux fois par le meme etat sans avoir touche au canvas.
   */
  const previewRef = useRef(onPreview);
  previewRef.current = onPreview;
  useEffect(() => {
    if (!watched || frozen || waiting !== null) return;

    let signature = '';
    const tick = (): void => {
      const engine = engineRef.current;
      const emit = previewRef.current;
      if (engine === null || emit === undefined || submittedRef.current) return;

      const drawing = engine.getDrawing();
      const next = `${drawing.length}:${drawing.reduce((sum, stroke) => sum + stroke.points.length, 0)}`;
      if (next === signature) return;
      signature = next;

      emit({ buffer: encodeDrawing(fitToBudget(drawing, MAX_DRAWING_BYTES)), palette: engine.getPalette() });
    };

    const timer = window.setInterval(tick, PREVIEW_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [watched, frozen, waiting]);

  // Le serveur a clos la phase : on rend ce qu'on a, tout de suite. submit()
  // est idempotent, un envoi deja parti par le timer ne repart pas.
  useEffect(() => {
    if (deadlinePassed) submit();
  }, [deadlinePassed, submit]);

  // Rendu deja parti (reconnexion, soumission par le timer) : le moteur doit
  // refuser tout nouveau trait meme si submit() n'est pas passe par ici.
  useEffect(() => {
    if (waiting === null) return;
    submittedRef.current = true;
    setFrozen(true);
    engineRef.current?.freeze();
  }, [waiting]);

  const selectTool = useCallback((next: ToolId) => {
    setToolState(next);
    engineRef.current?.setTool(next);
  }, []);

  // Choisir une couleur quand la gomme ou la pipette est active revient au
  // pinceau. Le calcul se fait ici et pas dans un updater de state : React
  // appelle les updaters deux fois en StrictMode, un effet de bord y serait
  // joue deux fois.
  const selectColor = useCallback(
    (index: number) => {
      // Une couleur choisie referme le panneau : on revient dessiner aussitot.
      setPanel('none');
      setColorIndexState(index);
      setCurrentColor(palette[index] ?? '#ffffff');
      engineRef.current?.setColorIndex(index);
      if (tool === 'eyedropper') {
        setToolState('brush');
        engineRef.current?.setTool('brush');
      }
    },
    [tool, palette],
  );

  const selectSize = useCallback((index: number) => {
    engineRef.current?.setBrushSizeIndex(index);
    engineRef.current?.setTool('brush');
    setToolState('brush');
    setBrushSize(BRUSH_SIZES[index] ?? 10);
  }, []);

  /** Slider : taille exacte, qui prime sur les trois presets. */
  const changeBrushSize = useCallback((size: number) => {
    setBrushSize(size);
    engineRef.current?.setBrushSize(size);
    if (engineRef.current !== null) {
      engineRef.current.setTool('brush');
      setToolState('brush');
    }
  }, []);

  const undo = useCallback(() => engineRef.current?.undo(), []);

  /**
   * Couleur libre. Elle rejoint la palette declaree avec le dessin, exactement
   * comme un prelevement a la pipette : sans cela les autres clients la
   * rendraient avec un repli.
   */
  const pickFreeColor = useCallback((color: string) => {
    setPanel('none');
    engineRef.current?.useColor(color);
  }, []);

  // --- Raccourcis clavier ---------------------------------------------------
  // Alt OU Ctrl maintenu preleve une couleur puis rend la main a l'outil
  // precedent : c'est le geste de tous les logiciels de peinture, et il evite
  // l'aller-retour par la barre d'outils au milieu d'un trait. Les deux touches
  // sont acceptees parce que la convention differe d'un logiciel a l'autre.
  const toolBeforeAlt = useRef<ToolId | null>(null);
  const PICK_KEYS = ['Alt', 'Control', 'Meta'];
  useEffect(() => {
    if (waiting !== null || frozen) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (isTyping(event.target) || event.repeat) return;

      // Modificateur seul maintenu : bascule temporaire sur la pipette.
      if (PICK_KEYS.includes(event.key)) {
        if (toolBeforeAlt.current === null) {
          event.preventDefault();
          toolBeforeAlt.current = tool;
          selectTool('eyedropper');
        }
        return;
      }

      // Une vraie combinaison (Ctrl+Z...) annule la bascule : on ne voulait pas
      // la pipette, on voulait le raccourci.
      if (toolBeforeAlt.current !== null) {
        selectTool(toolBeforeAlt.current);
        toolBeforeAlt.current = null;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const size = sizeShortcut(event);
      if (size !== null) {
        selectSize(size);
        event.preventDefault();
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'b':
          selectTool('brush');
          break;
        case 'p':
          selectTool('fill');
          break;
        case 'i':
          selectTool('eyedropper');
          break;
        case 'h':
          selectTool('pan');
          break;
        default:
          return;
      }
      event.preventDefault();
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (!PICK_KEYS.includes(event.key) || toolBeforeAlt.current === null) return;
      selectTool(toolBeforeAlt.current);
      toolBeforeAlt.current = null;
    };

    // Perdre le focus pendant qu'Alt est enfonce ne doit pas coincer la pipette.
    const onBlur = (): void => {
      if (toolBeforeAlt.current === null) return;
      selectTool(toolBeforeAlt.current);
      toolBeforeAlt.current = null;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [tool, waiting, frozen, selectTool, selectSize, undo]);

  // Le curseur du systeme n'est masque que quand le moteur en dessine un a la
  // place, c'est-a-dire tant qu'on peut tracer. Une fois le dessin rendu, plus
  // personne ne dessine le pointeur : le masquer le faisait disparaitre.
  const drawable = waiting === null && !frozen;
  /**
   * Hors de la zone, le pinceau et le pot deplacent la vue : le curseur doit le
   * dire AVANT le geste, sinon la seule facon de l'apprendre est d'essayer.
   * La pipette est exclue : elle preleve sur tout le tableau, elle travaille
   * donc dehors autant que dedans.
   */
  const pans = tool === 'pan' || (outsideZone && (tool === 'brush' || tool === 'fill'));
  const canvasClass = [
    'board__canvas',
    drawable && tool === 'brush' && !outsideZone ? 'board__canvas--hidden-cursor' : '',
    drawable && pans ? 'board__canvas--grab' : '',
  ]
    .filter((name) => name !== '')
    .join(' ');

  /** Libelle + raccourci, pour l'infobulle et le lecteur d'ecran. */
  const hint = (label: string, key: string): string => `${label} (${key})`;

  return (
    <div className="board">
      <canvas ref={canvasRef} className={canvasClass} />

      <div className="board__hud">
        <span className="board__hint">
          {roundLabel !== undefined && <strong className="board__round">{roundLabel}</strong>}
          {phase === 'overview' ? t('drawing.locating') : t('drawing.instruction')}
        </span>
        {endsAt === null ? (
          <TimerRing durationMs={durationMs} running={!frozen} onExpire={submit} />
        ) : (
          // Le compte a rebours CONTINUE apres la soumission : il dit combien de
          // temps dure encore l'attente. Le figer laissait un chiffre mort a
          // l'ecran, qu'on lisait comme une partie bloquee. Seuls le tic
          // d'urgence et la soumission automatique s'arretent : ils n'ont plus
          // d'objet une fois le dessin parti.
          <PhaseTimer
            endsAt={endsAt}
            durationMs={durationMs}
            {...(frozen ? {} : { onExpire: submit, tick: true })}
          />
        )}
      </div>

      {/* Sur l'image, en bas : c'est la vue qu'il regle, pas la peinture. */}
      <ZoomControl zoom={zoom} onZoom={(factor) => engineRef.current?.setZoomFactor(factor)} />

      {waiting !== null ? (
        <div className="toolbar toolbar--waiting">{waiting}</div>
      ) : compact ? (
        /* ECRAN ETROIT : une seule rangee de commandes, et des panneaux qui
           s'ouvrent PAR-DESSUS le tableau. La barre pleine prenait pres de la
           moitie de la hauteur d'un telephone, alors qu'on y a besoin de
           surface pour viser au doigt. Les panneaux ne poussent rien : le
           canvas garde sa taille, donc le dessin ne bouge pas sous la main. */
        <div className="toolbar toolbar--compact">
          {panel !== 'none' && (
            <>
              <button
                type="button"
                className="panel__scrim"
                aria-label={t('common.close')}
                onClick={() => setPanel('none')}
              />
              <div className="panel" role="dialog" aria-label={t(PANEL_LABEL[panel])}>
                {panel === 'color' && (
                  <div className="panel__scroll">
        <div className="toolbar__palette">
                    {palette.slice(0, swatchCount.current ?? palette.length).map((color, index) => (
                      <span className="swatch-wrap" key={`${color}-${index}`}>
                        <button
                          type="button"
                          className="swatch"
                          style={{ background: color }}
                          aria-label={t('tool.color', { n: index + 1 })}
                          aria-pressed={colorIndex === index}
                          onClick={() => selectColor(index)}
                        />
                      </span>
                    ))}

                    {/* Le FOND remplace la gomme. Il fait le meme geste : la zone part
                        de cette couleur, repeindre avec elle EST l'effacement, a ceci
                        pres que c'est un trait comme un autre, qui s'annule et se
                        recouvre. Une gomme, elle, demandait de comprendre qu'elle
                        rendait un fond que le joueur n'avait jamais vu.
                        Sa couleur suit celle de la zone : une pastille blanche sur un
                        fond ocre aurait promis un effacement qu'elle ne fait pas. */}
                    <span className="swatch-wrap">
                      <button
                        type="button"
                        className="swatch swatch--base"
                        style={{ background: baseColor }}
                        aria-label={t('tool.base')}
                        title={t('tool.base')}
                        aria-pressed={colorIndex < 0 && currentColor.toLowerCase() === baseColor}
                        onClick={() => pickFreeColor(baseColor)}
                      />
                    </span>

                    {/* Selecteur libre, en fin de palette : le natif fait le travail et
                        se comporte comme le joueur l'attend sur chaque plateforme. */}
                    <span className="swatch-wrap">
                      <input
                        type="color"
                        className="swatch swatch--picker"
                        aria-label={t('tool.freeColor')}
                        title={t('tool.freeColor')}
                        value={currentColor}
                        onChange={(event) => pickFreeColor(event.target.value)}
                      />
                    </span>
                  </div>
                  </div>
                )}
                {panel === 'tool' && (
                  <div className="panel__tools">
                    <button
                      type="button"
                      className="tool-button tool-button--icon"
                      aria-label={t('tool.brush')}
                      aria-pressed={tool === 'brush'}
                      onClick={() => {
                        selectTool('brush');
                        setPanel('none');
                      }}
                    >
                      <BrushIcon />
                    </button>
                    <button
                      type="button"
                      className="tool-button tool-button--icon"
                      aria-label={t('tool.fill')}
                      aria-pressed={tool === 'fill'}
                      onClick={() => {
                        selectTool('fill');
                        setPanel('none');
                      }}
                    >
                      <FillIcon />
                    </button>
                    <button
                      type="button"
                      className="tool-button tool-button--icon"
                      aria-label={t('tool.eyedropper')}
                      aria-pressed={tool === 'eyedropper'}
                      onClick={() => {
                        selectTool('eyedropper');
                        setPanel('none');
                      }}
                    >
                      <EyedropperIcon />
                    </button>
                    <button
                      type="button"
                      className="tool-button tool-button--icon"
                      aria-label={t('tool.pan')}
                      aria-pressed={tool === 'pan'}
                      onClick={() => {
                        selectTool('pan');
                        setPanel('none');
                      }}
                    >
                      <HandIcon />
                    </button>
                  </div>
                )}
                {panel === 'size' && (
                  <div className="panel__size">
                    <div className="tool-group">
                      {BRUSH_SIZES.map((size, index) => (
                        <button
                          key={size}
                          type="button"
                          className="tool-button tool-button--icon"
                          aria-label={t('tool.brushSize', { n: index + 1 })}
                          aria-pressed={tool === 'brush' && sizeIndex === index}
                          onClick={() => selectSize(index)}
                        >
                          <span
                            className="tool-button__dot"
                            style={{ width: 4 + index * 6, height: 4 + index * 6 }}
                          />
                        </button>
                      ))}
                    </div>
                    <label className="size-slider">
                      <input
                        type="range"
                        min={MIN_BRUSH_SIZE}
                        max={MAX_BRUSH_SIZE}
                        value={brushSize}
                        aria-label={t('tool.size')}
                        onChange={(event) => changeBrushSize(Number(event.target.value))}
                      />
                      <span className="size-slider__value mono-tabular">{brushSize}</span>
                    </label>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="toolbar__row">
            <button
              type="button"
              className="tool-button tool-button--icon"
              aria-label={t('tool.colors')}
              aria-pressed={panel === 'color'}
              onClick={() => setPanel(panel === 'color' ? 'none' : 'color')}
            >
              <span className="swatch swatch--mini" style={{ background: currentColor }} />
            </button>

            {/* La pipette a son propre bouton, contre la palette : c'est d'elle
                qu'on tire les couleurs du tableau, et rangee au fond d'un
                panneau elle passait inapercue alors qu'elle est l'outil qui
                fait la difference entre une retouche qui se fond et une qui se
                voit. */}
            <button
              type="button"
              className="tool-button tool-button--icon"
              aria-label={t('tool.eyedropper')}
              aria-pressed={tool === 'eyedropper'}
              onClick={() => selectTool(tool === 'eyedropper' ? 'brush' : 'eyedropper')}
            >
              <EyedropperIcon />
            </button>

            <button
              type="button"
              className="tool-button tool-button--icon"
              aria-label={t('tool.tools')}
              aria-pressed={panel === 'tool'}
              // Teinte quand un outil autre que le pinceau est actif : l'icone
              // etant fixe, c'est elle qui dit qu'on n'est plus au pinceau.
              data-active={tool !== 'brush'}
              onClick={() => setPanel(panel === 'tool' ? 'none' : 'tool')}
            >
              <ToolboxIcon />
            </button>

            <button
              type="button"
              className="tool-button tool-button--icon"
              aria-label={t('tool.size')}
              aria-pressed={panel === 'size'}
              onClick={() => setPanel(panel === 'size' ? 'none' : 'size')}
            >
              <span
                className="tool-button__dot"
                style={{ width: 4 + sizeIndex * 6, height: 4 + sizeIndex * 6 }}
              />
            </button>

            <button
              type="button"
              className="tool-button tool-button--icon"
              aria-label={t('tool.undo')}
              disabled={!canUndo}
              onClick={undo}
            >
              <UndoIcon />
            </button>
          </div>

          <button type="button" className="button-primary toolbar__submit" onClick={submit}>
            <CheckIcon />
            {submitLabel ?? t('drawing.submit')}
          </button>
        </div>
      ) : (
        <div className="toolbar">
          <div className="toolbar__palette">
            {palette.slice(0, swatchCount.current ?? palette.length).map((color, index) => (
              <span className="swatch-wrap" key={`${color}-${index}`}>
                <button
                  type="button"
                  className="swatch"
                  style={{ background: color }}
                  aria-label={t('tool.color', { n: index + 1 })}
                  aria-pressed={colorIndex === index}
                  onClick={() => selectColor(index)}
                />
              </span>
            ))}

            {/* Le blanc remplace la gomme. Il fait le meme geste : la zone part
                blanche, repeindre en blanc EST l'effacement, a ceci pres que
                c'est un trait comme un autre, qui s'annule et se recouvre. Une
                gomme, elle, demandait de comprendre qu'elle rendait un fond que
                le joueur n'avait jamais vu. */}
            <span className="swatch-wrap">
              <button
                type="button"
                className="swatch swatch--base"
                        style={{ background: baseColor }}
                aria-label={t('tool.base')}
                title={t('tool.base')}
                aria-pressed={colorIndex < 0 && currentColor.toLowerCase() === baseColor}
                onClick={() => pickFreeColor(baseColor)}
              />
            </span>

            {/* Selecteur libre, en fin de palette : le natif fait le travail et
                se comporte comme le joueur l'attend sur chaque plateforme. */}
            <span className="swatch-wrap">
              <input
                type="color"
                className="swatch swatch--picker"
                aria-label={t('tool.freeColor')}
                title={t('tool.freeColor')}
                value={currentColor}
                onChange={(event) => pickFreeColor(event.target.value)}
              />
            </span>
          </div>

          <div className="toolbar__tools">
            {/* Le curseur de taille vit SOUS les trois presets, et pas sur toute
                la largeur : etire au bas de l'ecran, il se lisait comme un
                reglage de zoom. Colle a eux, il ne peut designer qu'une chose,
                et les presets s'allument selon sa valeur. */}
            <div className="tool-stack">
              <div className="tool-group">
                {BRUSH_SIZES.map((size, index) => (
                  <button
                    key={size}
                    type="button"
                    className="tool-button tool-button--icon"
                    title={hint(t('tool.brushSize', { n: index + 1 }), String(index + 1))}
                    aria-label={t('tool.brushSize', { n: index + 1 })}
                    aria-pressed={tool === 'brush' && sizeIndex === index}
                    onClick={() => selectSize(index)}
                  >
                    <span
                      className="tool-button__dot"
                      style={{ width: 4 + index * 6, height: 4 + index * 6 }}
                    />
                  </button>
                ))}
              </div>

              {/* Les chiffres des raccourcis, HORS du cadre du groupe et alignes
                  sous chaque bouton. Poses a l'interieur, ils faisaient grandir
                  le cadre et se lisaient comme une partie du reglage ; ici ils
                  se tiennent comme « Alt maintenu : pipette » sous les outils,
                  au meme format.

                  La rangee reprend le pas du groupe (meme gouttiere, meme
                  rembourrage) et chaque case la largeur d'un bouton : c'est ce
                  qui garde les chiffres en face de leur pastille sans rien
                  mesurer au rendu.

                  `aria-hidden` : le raccourci est deja dans le `title` et dans
                  l'`aria-label` de chaque bouton, le relire en trois chiffres
                  orphelins n'apprendrait rien a un lecteur d'ecran. */}
              <div className="tool-keys" aria-hidden="true">
                {BRUSH_SIZES.map((size, index) => (
                  <span key={size} data-active={tool === 'brush' && sizeIndex === index}>
                    {index + 1}
                  </span>
                ))}
              </div>

              <label className="size-slider">
                <input
                  type="range"
                  min={MIN_BRUSH_SIZE}
                  max={MAX_BRUSH_SIZE}
                  value={brushSize}
                  aria-label={t('tool.size')}
                  onChange={(event) => changeBrushSize(Number(event.target.value))}
                />
                <span className="size-slider__value mono-tabular">{brushSize}</span>
              </label>
            </div>

            {/* Meme forme que la pile des tailles : le groupe, et sa legende
                juste dessous. L'indice se place donc a la hauteur du curseur de
                taille, centre sur les outils qu'il concerne. Il est en absolu :
                le texte est bien plus large que le groupe, dans le flux il
                ecarterait toute la rangee. */}
            <div className="tool-stack tool-stack--captioned">
              <div className="tool-group">
                <button
                  type="button"
                  className="tool-button tool-button--icon"
                  title={hint(t('tool.fill'), 'P')}
                  aria-label={t('tool.fill')}
                  aria-pressed={tool === 'fill'}
                  onClick={() => selectTool('fill')}
                >
                  <FillIcon />
                </button>
                <button
                  type="button"
                  className="tool-button tool-button--icon"
                  title={hint(t('tool.eyedropper'), `I ${t('tool.orHoldAlt')}`)}
                  aria-label={t('tool.eyedropper')}
                  aria-pressed={tool === 'eyedropper'}
                  onClick={() => selectTool('eyedropper')}
                >
                  <EyedropperIcon />
                </button>
                {/* Sans souris a deux doigts, c'est le seul moyen de deplacer la vue. */}
                <button
                  type="button"
                  className="tool-button tool-button--icon"
                  title={hint(t('tool.pan'), 'H')}
                  aria-label={t('tool.pan')}
                  aria-pressed={tool === 'pan'}
                  onClick={() => selectTool('pan')}
                >
                  <HandIcon />
                </button>
              </div>

              {/* Seul ce geste est annonce : les touches d'outil restent dans
                  les infobulles, une liste de raccourcis se lirait comme un
                  mode d'emploi. Masque sans pointeur fin, un raccourci clavier
                  n'ayant rien a dire sur telephone. */}
              <p className="toolbar__shortcut">{t('tool.eyedropperShortcut')}</p>
            </div>

            <div className="tool-group">
              <button
                type="button"
                className="tool-button tool-button--icon"
                title={hint(t('tool.undo'), 'Ctrl+Z')}
                aria-label={t('tool.undo')}
                disabled={!canUndo}
                onClick={undo}
              >
                <UndoIcon />
              </button>
            </div>
          </div>

          {/* Valider ferme le dessin pour ce joueur ; quand tous ont valide, la
              manche avance sans attendre la fin du timer. */}
          <button type="button" className="button-primary toolbar__submit" onClick={submit}>
            <CheckIcon />
            {submitLabel ?? t('drawing.submit')}
          </button>
        </div>
      )}
    </div>
  );
}
